import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
} from '@tanstack/react-query';
import { api } from '../../core/client';
import type { AppNotification, NotificationFilters, NotificationPage } from './notifications.types';

const BASE = '/users/v1/notifications';
const KEY = ['notifications'] as const;

/** The bell poll (D-066): 30s keeps the badge honest without WS plumbing. */
const POLL_MS = 30_000;

export function useNotifications(enabled = true) {
  return useQuery({
    queryKey: [...KEY, 'list'],
    queryFn: () => api.get<AppNotification[]>(`${BASE}?limit=30`),
    refetchInterval: POLL_MS,
    enabled,
  });
}

export function useUnreadCount(enabled = true) {
  return useQuery({
    queryKey: [...KEY, 'unread'],
    queryFn: () => api.get<number>(`${BASE}/unread-count`),
    refetchInterval: POLL_MS,
    enabled,
  });
}

/** One page of the inbox: `cursor` empty = the newest. Shared by the paged query and
 *  the head poll below, so the two can never disagree on the URL. */
function fetchInboxPage(filters: NotificationFilters, cursor?: string) {
  const q = new URLSearchParams();
  if (filters.unread) q.set('unread', 'true');
  if (filters.severity) q.set('severity', filters.severity);
  if (filters.kind_prefix) q.set('kind_prefix', filters.kind_prefix);
  if (filters.site_id) q.set('site_id', filters.site_id);
  q.set('limit', '30');
  if (cursor) q.set('cursor', cursor);
  return api.get<NotificationPage>(`${BASE}/page?${q.toString()}`);
}

/** Dedicated inbox page (R-112) — keyset-paginated history with filters. */
export function useNotificationsPaged(filters: NotificationFilters) {
  return useInfiniteQuery({
    queryKey: [...KEY, 'page', filters],
    queryFn: ({ pageParam }) => fetchInboxPage(filters, pageParam as string),
    initialPageParam: '',
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    staleTime: 10_000,
  });
}

/** The inbox's NEWEST page, polled — what tells the open inbox that notifications
 *  arrived while it was on screen. A separate query on purpose: re-polling the paged
 *  query itself would refetch every page it holds from its old cursors, and rows
 *  that arrived since would push older ones across a page boundary and lose them. */
export function useNotificationsHead(filters: NotificationFilters, enabled = true) {
  return useQuery({
    queryKey: [...KEY, 'head', filters],
    queryFn: () => fetchInboxPage(filters),
    refetchInterval: POLL_MS,
    enabled,
  });
}

/** Puts rows the head poll found at the TOP of the paged inbox's first page — no
 *  refetch, no lost scroll position, and the next-page cursor stays valid because it
 *  belongs to the OLDEST row, which has not changed. */
export function usePrependNotifications(filters: NotificationFilters) {
  const qc = useQueryClient();
  return (rows: AppNotification[]) =>
    qc.setQueryData<InfiniteData<NotificationPage>>([...KEY, 'page', filters], (d) => {
      const [first, ...rest] = d?.pages ?? [];
      if (!d || !first) return d;
      return { ...d, pages: [{ ...first, rows: [...rows, ...first.rows] }, ...rest] };
    });
}

/** A refresh somebody asked for: back to the NEWEST page, fresh. Refetching a
 *  multi-page infinite query re-requests every page from its OLD cursors, and rows
 *  that arrived since would push older ones across a boundary and drop them. So the
 *  newest page is fetched on its own and then REPLACES the pages in one write: the
 *  rows already on screen stay put until it lands (one loader, the caller's), and the
 *  paged query itself never goes into a fetching state of its own. */
export function useRefreshNotificationInbox(filters: NotificationFilters) {
  const qc = useQueryClient();
  return async () => {
    const [fresh] = await Promise.all([
      fetchInboxPage(filters),
      qc.refetchQueries({ queryKey: [...KEY, 'head'] }),
      qc.refetchQueries({ queryKey: [...KEY, 'unread'] }),
    ]);
    qc.setQueryData<InfiniteData<NotificationPage>>([...KEY, 'page', filters], {
      pages: [fresh],
      pageParams: [''],
    });
  };
}

export function useMarkNotificationRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<boolean>(`${BASE}/${id}/read`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useMarkAllNotificationsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<number>(`${BASE}/read-all`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useAckNotification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<boolean>(`${BASE}/${id}/ack`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}
